import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useCreateConnection } from '@/queries/connections/useCreateConnection'
import { useGoogleAdsAccounts } from '@/queries/connections/useGoogleAdsAccounts'
import { useUpdateConnection } from '@/queries/connections/useUpdateConnection'
import { zodResolver } from '@hookform/resolvers/zod'
import { useStytchOrganization } from '@stytch/react/b2b'
import { AccessibleGoogleAccount } from 'honeygrid-types'
import {
	Connection,
	ConnectionPlatforms,
	ConnectionStatus,
	CreateConnection,
	UpdateConnection,
} from 'honeygrid-types/validators-and-types/ConnectionTypes'
import { useSnackbar } from 'notistack'
import { FC, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import { SidePanel } from '../SidePanel'
import { Combobox } from '../ui/combobox'

interface GoogleAdsConnectionDetailsProps {
	connection?: Connection
}

const googleAdsSchema = z.object({
	name: z.string().min(1, 'Connection name is required.'),
	accountId: z.string().min(1, 'An account must be selected.'),
	accountName: z.string().min(1, 'An account must be selected.'),
	managerAccountId: z.string().optional(),
	managerAccountName: z.string().optional(),
})

type FormValues = z.infer<typeof googleAdsSchema>

export const GoogleAdsConnectionDetails: FC<GoogleAdsConnectionDetailsProps> = ({ connection }) => {
	const { connectionId = '' } = useParams()
	const { organization } = useStytchOrganization()
	const { enqueueSnackbar } = useSnackbar()

	const { data: accountsData, isLoading: isLoadingAccounts } = useGoogleAdsAccounts({
		workspaceId: organization?.organization_id,
		connectionId,
	})

	const createConnectionMutation = useCreateConnection()
	const updateConnectionMutation = useUpdateConnection()

	// Type the platform data for better type safety
	const platformData = connection?.platform_data as
		| {
				accountId?: string
				accountName?: string
				managerAccountId?: string
				managerAccountName?: string
		  }
		| undefined

	const form = useForm<FormValues>({
		resolver: zodResolver(googleAdsSchema),
		defaultValues: {
			name: connection?.name ?? '',
			accountId: platformData?.accountId ?? '',
			accountName: platformData?.accountName ?? '',
			managerAccountId: platformData?.managerAccountId ?? '',
			managerAccountName: platformData?.managerAccountName ?? '',
		},
	})

	const onSubmit = async (values: FormValues) => {
		if (connectionId) {
			// Update existing connection - only pass the fields that can be updated
			const updatePayload: Partial<UpdateConnection> = {
				name: values.name,
				platform_data: {
					accountId: values.accountId,
					accountName: values.accountName,
					managerAccountId: values.managerAccountId,
					managerAccountName: values.managerAccountName,
				},
			}

			if (connection?.status === ConnectionStatus.Pending && values.accountId) {
				updatePayload.status = ConnectionStatus.Active
			}

			await updateConnectionMutation.mutateAsync({
				workspaceId: organization?.organization_id,
				connectionId,
				connection: updatePayload,
			})
			enqueueSnackbar('Connection updated', { variant: 'success' })
		} else {
			// Create new connection - provide all required fields
			// Note: workspace_id and created_by are populated by the backend from request context
			const createPayload = {
				name: values.name,
				platform: ConnectionPlatforms.GoogleAds, // This will be 'google_ads' (snake_case)
				auth_type: 'oauth2' as const,
				status: 'pending' as const, // New connections start as pending until OAuth is complete
				// Store account information in platform_data
				platform_data: {
					accountId: values.accountId,
					accountName: values.accountName,
					managerAccountId: values.managerAccountId,
					managerAccountName: values.managerAccountName,
				},
				// For OAuth2, we need proper auth_data structure - will be populated during OAuth flow
				auth_data: {
					access_token: '', // Will be populated during OAuth
					token_type: 'Bearer',
					refresh_token: '', // Will be populated during OAuth
					expires_at: '', // Will be populated during OAuth
					scopes: ['https://www.googleapis.com/auth/adwords'],
				},
			} as unknown as CreateConnection // Type assertion since backend populates workspace_id and created_by

			await createConnectionMutation.mutateAsync({
				workspaceId: organization?.organization_id,
				connection: createPayload,
			})
			enqueueSnackbar('Connection created', { variant: 'success' })
		}
	}

	const flattenedAccounts = useMemo(() => {
		const result: (Omit<AccessibleGoogleAccount, 'customerClients'> & {
			managerAccountId?: string
			managerAccountName?: string
		})[] = []
		const seenIds = new Set<string>()
		function flatten(accounts: AccessibleGoogleAccount[], parent?: AccessibleGoogleAccount | null) {
			for (const account of accounts) {
				if (seenIds.has(account.id)) {
					continue
				}
				seenIds.add(account.id)

				const { customerClients, ...rest } = account
				result.push({
					...rest,
					managerAccountId: parent?.id,
					managerAccountName: parent?.descriptiveName,
				})
				if (customerClients && customerClients.length > 0) {
					flatten(customerClients as AccessibleGoogleAccount[], account)
				}
			}
		}

		flatten(accountsData?.accounts ?? [])
		return result
	}, [accountsData])

	useEffect(() => {
		if (connection) {
			form.setValue('name', connection.name)
			form.setValue('accountId', platformData?.accountId ?? '')
			form.setValue('accountName', platformData?.accountName ?? '')
			form.setValue('managerAccountId', platformData?.managerAccountId ?? '')
			form.setValue('managerAccountName', platformData?.managerAccountName ?? '')
		}
	}, [connection, form, platformData])

	const handleAccountSelect = (accountId: string) => {
		const selected = flattenedAccounts.find((a) => a.id === accountId)
		if (selected) {
			form.setValue('accountId', selected.id)
			form.setValue('accountName', selected.descriptiveName)
			form.setValue('managerAccountId', selected.managerAccountId ?? undefined)
			form.setValue('managerAccountName', selected.managerAccountName ?? undefined)
			if (!form.getValues('name')) {
				form.setValue('name', selected.descriptiveName)
			}
		}
	}

	const isSaving = createConnectionMutation.isPending || updateConnectionMutation.isPending

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
				<SidePanel
					header={{
						title: 'Connections',
						backEnabled: true,
					}}
					footer={{
						buttonLabel: 'Save Connection',
						disabled: isSaving,
						buttonType: 'submit',
					}}
				>
					<div className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Connection name</FormLabel>
									<FormControl>
										<Input placeholder="Enter a name" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="accountId"
							render={({ field }) => (
								<FormItem className="flex flex-col">
									<FormLabel>Google Ads Account</FormLabel>
									<Combobox
										isLoading={isLoadingAccounts}
										items={flattenedAccounts.map((account) => ({
											value: account.id,
											label: `${account.descriptiveName} ${
												account.managerAccountName ? ` (${account.managerAccountName})` : ''
											}`,
										}))}
										value={field.value}
										onValueChange={(value) => {
											field.onChange(value)
											handleAccountSelect(value)
										}}
										disabled={isLoadingAccounts}
										placeholder="Select a Google Ads account"
									/>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</SidePanel>
			</form>
		</Form>
	)
}
